import { InertiaLink } from '@inertiajs/inertia-react';
import cx from 'classnames';
import React from 'react';

export enum ButtonType {
  DEFAULT = 'default',
  CTA = 'cta',
  DISABLED = 'disabled',
  OUTLINE_WHITE = 'outline-white',
  OUTLINE_BLUE = 'outline-blue',
}

const TYPE_TO_CLASSES = {
  [ButtonType.DEFAULT]: 'bg-smurfs-gold text-white',
  [ButtonType.CTA]: 'bg-slightly-off-white text-almost-black',
  [ButtonType.DISABLED]: 'bg-gray-200 text-gray-500 cursor-not-allowed',
  [ButtonType.OUTLINE_WHITE]: 'border-2 border-white text-white',
  [ButtonType.OUTLINE_BLUE]: 'border-2 border-blue-500 text-blue-500',
};

export enum ButtonSize {
  DEFAULT = 'default',
  SMALL = 'small',
}

const SIZE_TO_CLASSES = {
  [ButtonSize.DEFAULT]: 'h-10',
  [ButtonSize.SMALL]: 'h-8',
};

const SIZE_TO_MIN_WIDTH = {
  [ButtonSize.DEFAULT]: 150,
  [ButtonSize.SMALL]: 100,
};

interface IBaseButton {
  children: string;
  size?: ButtonSize;
  type?: ButtonType;
  className?: string;
  disabled?: boolean;
}

interface ISimpleButton extends IBaseButton {
  onClick: () => void;
}

interface IInertiaButton extends IBaseButton {
  href: string;
  method?: string;
  preserveScroll?: boolean;
  data?: object;
}

interface IExternalLinkButton extends IBaseButton {
  href: string;
  external: true;
}

type IProps = ISimpleButton | IInertiaButton | IExternalLinkButton;

export function Button(props: IProps) {
  const {
    disabled,
    size = ButtonSize.DEFAULT,
    type = ButtonType.DEFAULT,
    className,
    children,
  } = props;

  const finalClassName = cx(
    'inline-block rounded cursor-pointer',
    !disabled && 'hover:shadow-md',
    SIZE_TO_CLASSES[size],
    TYPE_TO_CLASSES[disabled ? ButtonType.DISABLED : type],
    className,
  );

  const renderedChildren = (
    <div
      className={cx(
        'h-full w-full flex items-center justify-center',
        'px-50 text-center text-16',
      )}
      style={{ minWidth: SIZE_TO_MIN_WIDTH[size] }}
    >
      {children}
    </div>
  );

  if (isExternalLinkButton(props)) {
    const { href } = props;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={finalClassName}
      >
        {renderedChildren}
      </a>
    );
  }

  if (isInertiaButton(props)) {
    const { href, method, preserveScroll, data } = props;
    return (
      <InertiaLink
        href={href}
        method={method}
        data={data}
        preserveScroll={preserveScroll}
        className={finalClassName}
      >
        {renderedChildren}
      </InertiaLink>
    );
  }

  const { onClick } = props;
  return (
    <div onClick={disabled ? undefined : onClick} className={finalClassName}>
      {renderedChildren}
    </div>
  );
}

/**
 * Return whether a Button should be rendered as an InertiaLink or not
 * @param props
 */
function isInertiaButton(props: any): props is IInertiaButton {
  return props.href !== undefined;
}

function isExternalLinkButton(props: any): props is IExternalLinkButton {
  return props.external === true;
}
